import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from '../../api/apiClient';
import { token, domain } from '../Security';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import './../../App.css';
import { TAXABLESTATUS } from '../../utils/enums';

// Common Components
import {
	CreateTableNew as CreateTable,
	SearchBar,
	Button,
	GeneralMessage,
	useGeneralMessage,
} from '../../common/APICommon';
import { Input } from '../../common/Input';
import { LoadingSpinner } from '../../common/Loading';

// Specific Logic
import { SetAuditTrail } from './../Reports/AuditTrailReportEmployeeSetup';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

// Icons
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from "@mui/icons-material/Close";
import SettingsIcon from '@mui/icons-material/Settings';
import DownloadIcon from '@mui/icons-material/Download';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import { EmployeePickerField } from '../../common/selectComponents/EmployeePicker';

function EmployeeBankAccount({ user, isHideSideMenu }) {
	const { showMessage, message, isSuccess, showGeneralMessage } = useGeneralMessage();

	// --- TAB STATE ---
	const [activeTab, setActiveTab] = useState(0); // 0 = Bank List, 1 = Employee Accounts

	// --- API URLS ---
	const apiUrlBank = domain + "/api/Bank";
	const apiUrlEmployeeAccount = domain + "/api/EmployeeBankAccount";

	// --- TABLE HEADERS ---
	const headersBank = ["ID", "Bank Name", "Status", "Actions"];
	const accessorsBank = ["id", "bankName", "isActive", "actions"];

	const headersAccount = ["ID", "Employee Id", "Employee Name", "Bank", "Account Number", "Taxable Status", "Actions"];
	const accessorsAccount = ["id", "employeePersonalInformationEmployeeId", "employeePersonalInformationFullName", "bankName", "bankAccount", "taxableStatus", "actions"];

	// --- STATE ---
	const [currentPage, setCurrentPage] = useState(1);
	const [loading, setLoading] = useState(false);
	const [loadingRequest, setLoadingRequest] = useState(false);

	// Data
	const [banks, setBanks] = useState([]);
	const [accounts, setAccounts] = useState([]);
	const [displayData, setDisplayData] = useState([]);
	const [filterData, setFilterData] = useState([]);

	// Forms & UI
	const [showAddForm, setShowAddForm] = useState(false);
	const [showEditForm, setShowEditForm] = useState(false);
	const [form, setForm] = useState({
		id: "",
		bankNameDescription: "",
		isActive: true,
		employeePersonalInformationId: "",
		employeePersonalInformationFullName: "",
		bankId: "",
		bankAccount: "",
		taxableStatus: TAXABLESTATUS.NONE
	});
	
	// Modals
	const [showUploadForm, setShowUploadForm] = useState(false);
	const [showSettings, setShowSettings] = useState(false);
	const settingsRef = useRef(null);

	// --- FETCH DATA ---

	const fetchBanks = useCallback(async () => {
		try {
			const response = await axios.get(apiUrlBank, { headers: { 'Authorization': `Bearer ${token}` } });
			setBanks(response.data);
			return response.data;
		} catch (error) {
			console.error('Error fetching banks:', error);
			return [];
		}
	}, [apiUrlBank]);

	const fetchAccounts = useCallback(async () => {
		try {
			const response = await axios.get(`${apiUrlEmployeeAccount}/username/${user.username}`, {
				headers: { 'Authorization': `Bearer ${token}` }
			});
			setAccounts(response.data);
			return response.data;
		} catch (error) {
			console.error('Error fetching accounts:', error);
			return [];
		}
	}, [apiUrlEmployeeAccount, user]);

	const fetchData = useCallback(async () => {
		setLoading(true);
		try {
			const bankList = await fetchBanks();

			if (activeTab === 0) {
				setDisplayData(bankList);
				setFilterData(bankList);
			} else {
				const accList = await fetchAccounts();
				const mappedAccounts = accList.map(acc => {
					const bank = bankList.find(b => b.id === acc.bankId);
					return {
						...acc,
						bankName: acc.bankName || (bank ? bank.bankName : "-"),
						bankId: acc.bankId,
						taxableStatus: acc.taxableStatus || TAXABLESTATUS.NONE
					};
				});
				setDisplayData(mappedAccounts);
				setFilterData(mappedAccounts);
			}
		} finally {
			setLoading(false);
		}
	}, [activeTab, fetchBanks, fetchAccounts]);

	useEffect(() => {
		fetchData();
		setCurrentPage(1);

		const handleClickOutside = (event) => {
			if (settingsRef.current && !settingsRef.current.contains(event.target)) {
				setShowSettings(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [fetchData]);

	// --- HANDLERS ---

	const handleTabSelect = (index) => {
		setActiveTab(index);
	};

	const handleFilter = (value) => {
		setCurrentPage(1);
		const lowerValue = value.toLowerCase();
		if (!value) {
			setDisplayData(filterData);
			return;
		}

		const res = filterData.filter((item) => {
			if (activeTab === 0) {
				return item.bankName?.toLowerCase().includes(lowerValue);
			} else {
				return (
					item.employeePersonalInformationFullName?.toLowerCase().includes(lowerValue) ||
					item.employeePersonalInformationEmployeeId?.toString().toLowerCase().includes(lowerValue) ||
					item.bankAccount?.toLowerCase().includes(lowerValue) ||
					item.bankName?.toLowerCase().includes(lowerValue)
				);
			}
		});
		setDisplayData(res);
	};

	const resetForm = () => {
		setForm({
			id: "",
			bankNameDescription: "",
			isActive: true,
			employeePersonalInformationId: "",
			employeePersonalInformationFullName: "",
			bankId: "",
			bankAccount: "",
			taxableStatus: TAXABLESTATUS.NONE
		});
	};

	// --- CRUD ---

	const handleSubmit = async (e) => {
		e.preventDefault();
		setLoadingRequest(true);
		try {
			let url = "";
			let payload = {};
			let auditTrailData = {};

			if (activeTab === 0) {
				url = apiUrlBank;
				payload = { bankName: form.bankNameDescription, isActive: form.isActive };
				auditTrailData = { field: "Add Bank", newValue: payload.bankName, oldValue: "-" };
			} else {
				url = apiUrlEmployeeAccount;
				payload = {
					employeePersonalInformationId: form.employeePersonalInformationId,
					bankId: form.bankId,
					bankAccount: form.bankAccount,
					taxableStatus: (form.taxableStatus === TAXABLESTATUS.NONE) ? null : form.taxableStatus
				};
				const bankName = banks.find(b => b.id == form.bankId)?.bankName;
				auditTrailData = { field: "Add Account", newValue: `${form.employeePersonalInformationFullName} - ${bankName}`, oldValue: "-" };
			}

			await axios.post(url, payload, { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } });
			await SetAuditTrail({ setupArea: "Employee Bank Account", actionTaken: "insert", ...auditTrailData, dateSaved: new Date(), savedById: user.id });

			showGeneralMessage("Record added successfully!", true);
			setShowAddForm(false);
			resetForm();
			fetchData();
		} catch (error) {
			console.error(error);
			showGeneralMessage(error.response?.data?.message || "Operation failed", false);
		} finally {
			setLoadingRequest(false);
		}
	};

	const handleUpdate = async (e) => {
		e.preventDefault();
		setLoadingRequest(true);
		try {
			let url = "";
			let payload = {};

			if (activeTab === 0) {
				url = `${apiUrlBank}/${form.id}`;
				payload = { id: form.id, bankName: form.bankNameDescription, isActive: form.isActive };
			} else {
				url = `${apiUrlEmployeeAccount}/${form.id}`;
				payload = {
					employeePersonalInformationId: form.employeePersonalInformationId,
					bankId: form.bankId,
					bankAccount: form.bankAccount,
					taxableStatus: (form.taxableStatus === TAXABLESTATUS.NONE) ? null : form.taxableStatus
				};
			}

			await axios.put(url, payload, { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } });
			showGeneralMessage("Record updated successfully!", true);
			setShowEditForm(false);
			resetForm();
			fetchData();
		} catch (error) {
			console.error(error);
			showGeneralMessage(error.response?.data?.message || "Operation failed", false);
		} finally {
			setLoadingRequest(false);
		}
	};

	const handleAddClick = () => {
		resetForm();
		setShowAddForm(true);
	};

	const handleEditClick = (item) => {
		if (activeTab === 0) {
			setForm({
				id: item.id,
				bankNameDescription: item.bankName,
				isActive: item.isActive
			});
		} else {
			setForm({
				id: item.id,
				employeePersonalInformationId: item.employeePersonalInformationId,
				employeePersonalInformationFullName: item.employeePersonalInformationFullName,
				bankId: item.bankId,
				bankAccount: item.bankAccount,
				taxableStatus: item.taxableStatus || TAXABLESTATUS.NONE,
				bankNameDescription: "", isActive: true
			});
		}
		setShowEditForm(true);
	};

	// --- EXCEL & UPLOAD LOGIC ---

	const exportToExcel = async () => {
		const workbook = new ExcelJS.Workbook();
		const sheetName = activeTab === 0 ? 'BankList' : 'EmployeeAccounts';
		const worksheet = workbook.addWorksheet(sheetName);

		let columns = [];
		if (activeTab === 0) {
			columns = [
				{ header: 'ID', key: 'id', width: 10 },
				{ header: 'Bank Name', key: 'bankName', width: 30 },
				{ header: 'Status', key: 'status', width: 15 },
			];
		} else {
			columns = [
				{ header: 'ID', key: 'id', width: 10 },
				{ header: 'Employee ID', key: 'empId', width: 15 },
				{ header: 'Employee Name', key: 'name', width: 30 },
				{ header: 'Bank', key: 'bank', width: 25 },
				{ header: 'Account Number', key: 'acc', width: 25 },
				{ header: 'Taxable Status', key: 'taxableStatus', width: 20 },
			];
		}

		worksheet.columns = columns;

		const headerRow = worksheet.getRow(1);
		headerRow.eachCell((cell) => {
			cell.font = { bold: true };
			cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '92d050' } };
		});

		displayData.forEach(item => {
			if (activeTab === 0) {
				worksheet.addRow({ id: item.id, bankName: item.bankName, status: item.isActive ? 'Active' : 'Inactive' });
			} else {
				worksheet.addRow({
					id: item.id,
					empId: item.employeePersonalInformationEmployeeId,
					name: item.employeePersonalInformationFullName,
					bank: item.bankName,
					acc: item.bankAccount,
					taxableStatus: item.taxableStatus
				});
			}
		});

		const buffer = await workbook.xlsx.writeBuffer();
		const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
		saveAs(blob, `${sheetName}.xlsx`);
	};

	const generateDownloadTemplate = async () => {
		const workbook = new ExcelJS.Workbook();
		const worksheet = workbook.addWorksheet('Template');
		const headerRow = worksheet.addRow(['EmployeeId', 'EmployeeName', 'BankAccount', 'BankName']);

		headerRow.eachCell((cell) => {
			cell.font = { bold: true };
			cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '92d050' } };
		});

		worksheet.columns = [{ width: 20 }, { width: 30 }, { width: 25 }, { width: 25 }];
		worksheet.addRow(["<EMP123>", "<DELA CRUZ, JUAN>", "<1234567890>", "<BDO>"]);

		const buffer = await workbook.xlsx.writeBuffer();
		const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
		saveAs(blob, 'Employee_Bank_Account_Template.xlsx');
		setShowSettings(false);
	};

	const handleFinalFileUpload = async (bulkData) => {
		setLoadingRequest(true);
		try {
			await axios.post(`${domain}/api/EmployeeBankAccount/bulk`, bulkData, {
				headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
			});
			await SetAuditTrail({
				setupArea: "Employee Bank Account", actionTaken: "upload", field: "Upload",
				newValue: `-`, oldValue: "-", dateSaved: new Date(), savedById: user.id
			});
			fetchData();
			showGeneralMessage("Upload successful!", true);
			setShowUploadForm(false);
		} catch (error) {
			console.error(error);
			showGeneralMessage(error.response?.data?.message || "Upload failed", false);
		} finally {
			setLoadingRequest(false);
		}
	};

	const actions = [
		{
			name: "Edit",
			icon: <EditIcon fontSize="small" />,
			className: "bg-green-500 hover:bg-green-600 border-none text-white",
			function: handleEditClick,
			visible: true
		}
	];

	const handleSelectEmployee = (employee) => {
		setForm(prev => ({ ...prev, employeePersonalInformationId: employee.id, employeePersonalInformationFullName: employee.fullName }));
	};

	return (
		<div>
			<div className={`MainBody row ${isHideSideMenu ? "MainBody2" : ""}`}>
				<div className="LeftSpace"></div>
				<div className={`BodySpace ${isHideSideMenu ? "BodySpace2" : ""}`}>
					<div className="MainteInfo shadow-xl">
						<div className="mainteHeader">
							<div className='sub-title row'>
								<span style={{ padding: "0 5px 0 0", color: "#b0b0b0" }}>{"Salary Module >"} </span>
								<span style={{ borderBottom: "1px solid black" }}>{"Employee Bank Account"}</span>
							</div>
							<div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
								<h3>Employee Bank Account</h3>
								<div className="relative" ref={settingsRef}>
									<button
										onClick={() => setShowSettings(!showSettings)}
										className="text-white bg-blue-500 hover:bg-bg-600 p-2 rounded-md cursor-pointer border-none"
										title="Settings"
									>
										<SettingsIcon />
									</button>
									{showSettings && (
										<div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-50 border border-gray-200">
											<div className="py-1">
												<button onClick={generateDownloadTemplate} className="flex items-center justify-start w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 border-none bg-transparent cursor-pointer">
													<DownloadIcon className="mr-2 text-blue-500" fontSize="small" />
													<span>Download Template</span>
												</button>
												<button onClick={() => { setShowUploadForm(true); setShowSettings(false); }} className="flex items-center justify-start w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 border-none bg-transparent cursor-pointer">
													<FileUploadIcon className="mr-2 text-green-500" fontSize="small" />
													<span>Upload Data</span>
												</button>
											</div>
										</div>
									)}
								</div>
							</div>
						</div>

						<div className='loan-tab p-4 space-y-4'>
							<Tabs selectedIndex={activeTab} onSelect={handleTabSelect}>
								<TabList>
									<Tab><b>BANKS (MASTER)</b></Tab>
									<Tab><b>EMPLOYEE ACCOUNTS</b></Tab>
								</TabList>

								<div className="mt-3 mb-5 flex justify-between items-center">
									<SearchBar handleFilter={handleFilter} />
									<div className="flex gap-2">
										<Button
											icon={<FileCopyIcon />}
											name="Extract"
											className="bg-orange-500 hover:bg-orange-600 border-none text-white"
											onClick={exportToExcel}
										/>
										<Button
											icon={<AddIcon />}
											name={activeTab === 0 ? "Add Bank" : "Add Account"}
											className="bg-customGreen hover:bg-customGreenHover border-none text-white"
											onClick={handleAddClick}
										/>
									</div>
								</div>

								<div className="loan-tabPanel">
									<TabPanel>
										<CreateTable
											tableName="Bank List"
											headers={headersBank}
											accessors={accessorsBank}
											data={displayData}
											actions={actions}
											currentPage={currentPage}
											setCurrentPage={setCurrentPage}
											itemsPerPage={10}
											totalItems={displayData.length}
										/>
									</TabPanel>
									<TabPanel>
										<CreateTable
											tableName="Employee Accounts"
											headers={headersAccount}
											accessors={accessorsAccount}
											data={displayData}
											actions={actions}
											currentPage={currentPage}
											setCurrentPage={setCurrentPage}
											itemsPerPage={10}
											totalItems={displayData.length}
										/>
									</TabPanel>
								</div>
							</Tabs>
						</div>
					</div>
				</div>
			</div>

			<AddEditForm
				isOpen={showAddForm || showEditForm}
				mode={showAddForm ? "add" : "edit"}
				onClose={() => { setShowAddForm(false); setShowEditForm(false); }}
				form={form}
				setForm={setForm}
				handleSubmit={showAddForm ? handleSubmit : handleUpdate}
				activeTab={activeTab}
				banks={banks}
				onClearEmployee={() => setForm(prev => ({ ...prev, employeePersonalInformationId: "", employeePersonalInformationFullName: "" }))}
				handleSelectEmployee={handleSelectEmployee}
			/>

			<UploadForm
				isOpen={showUploadForm}
				onClose={() => setShowUploadForm(false)}
				handleFinalFileUpload={handleFinalFileUpload}
				banks={banks}
			/>

			<GeneralMessage form={showMessage} message={message} isSuccess={isSuccess} />
			<LoadingSpinner loading={loadingRequest || loading} />
		</div>
	);
}

// --- SUB-COMPONENTS ---

function AddEditForm({
	isOpen,
	mode,
	onClose,
	form,
	setForm,
	handleSubmit,
	activeTab,
	banks,
	onClearEmployee,
	handleSelectEmployee
}) {
	const [errorState, setErrorState] = useState(false);

	const submit = (e) => {
		e.preventDefault();
		let isValid = true;
		if (activeTab === 0) {
			if (!form.bankNameDescription) isValid = false;
		} else {
			// Changed validation to allow 0 if that's a valid ID, and ensures !form.bankId doesn't trigger erroneously on update
			if (!form.employeePersonalInformationId || (!form.bankId && form.bankId !== 0) || !form.bankAccount) isValid = false;
		}
		if (!isValid) { setErrorState(true); return; }
		handleSubmit(e);
		setErrorState(false);
	};

	if (!isOpen) return null;

	const title = mode === "add"
		? (activeTab === 0 ? "Add New Bank" : "Add Employee Account")
		: (activeTab === 0 ? "Update Bank" : "Update Employee Account");

	return (
		<div className="fixed inset-0 z-[3000] bg-black/50 flex items-center justify-center p-3 text-gray-700 overflow-auto">
			<div className="bg-gray-50 w-full md:w-[90%] lg:w-[40%] rounded-lg shadow-xl flex flex-col relative max-h-[90vh]">
				<div className="flex justify-between items-center px-6 py-2 border-b border-gray-200 bg-white rounded-t-lg shadow-sm">
					<h2 className="text-xl font-semibold text-gray-600">{title}</h2>
					<button onClick={onClose} className="text-gray-400 hover:text-gray-700 border-none bg-transparent cursor-pointer"><CloseIcon /></button>
				</div>
				<div className="p-6 overflow-y-auto space-y-4">
					<div className="border border-gray-300 rounded-lg bg-white px-4 py-3 shadow-sm space-y-4">
						{activeTab === 0 ? (
							<>
								<Input
									label="Bank Name:"
									value={form.bankNameDescription}
									onChange={(e) => setForm({ ...form, bankNameDescription: e.target.value })}
									placeholder="Enter Bank Name"
									isRequired={true}
									error={errorState && !form.bankNameDescription}
									errorMessage="Required"
								/>
								<div className="flex items-center pt-2">
									<input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
									<label className="ml-2 text-sm font-medium text-gray-600">Active</label>
								</div>
							</>
						) : (
							<>
								<div className="flex flex-col space-y-1">
									{mode === "add" ? (
										<EmployeePickerField
											label="Employee:"
											employeePersonalInformationId={form?.employeePersonalInformationId}
											displayValue={`${form?.employeePersonalInformationFullName}`}
											onSelect={handleSelectEmployee}
											onClear={onClearEmployee}
											error={!form?.employeePersonalInformationId && errorState}
											errorMessage="Please select an employee"
											allowClear={false}
										/>
									) : (
										<Input label="Employee:" value={form.employeePersonalInformationFullName || ""} readOnly={true} className="bg-gray-100 cursor-not-allowed text-gray-500" />
									)}
								</div>
								<div className="flex flex-col">
									<label className="text-sm font-semibold text-gray-600 mb-1">Bank: <span className="text-red-500">*</span></label>
									<select
										className={`border rounded p-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 ${errorState && (!form.bankId && form.bankId !== 0) ? 'border-red-500' : 'border-gray-300'}`}
										value={form.bankId || ""}
										onChange={(e) => setForm({ ...form, bankId: e.target.value })}
									>
										<option value="" disabled>Select a Bank</option>
										{banks.filter(b => b.isActive || b.id == form.bankId).map((b) => (
											<option key={b.id} value={b.id}>{b.bankName}</option>
										))}
									</select>
									{errorState && (!form.bankId && form.bankId !== 0) && <span className="text-red-500 text-xs mt-1">Required</span>}
								</div>
								<Input
									label="Account Number:"
									value={form.bankAccount}
									onChange={(e) => setForm({ ...form, bankAccount: e.target.value })}
									placeholder="Enter Account Number"
									isRequired={true}
									error={errorState && !form.bankAccount}
									errorMessage="Required"
								/>

								{/* Taxable Status Select */}
								<div className="flex flex-col">
									<label className="text-sm font-semibold text-gray-600 mb-1">Taxable Status:</label>
									<select
										className="border rounded p-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 border-gray-300"
										value={form.taxableStatus || TAXABLESTATUS.NONE}
										onChange={(e) => setForm({ ...form, taxableStatus: e.target.value })}
									>
										{Object.values(TAXABLESTATUS).map((status) => (
											<option key={status} value={status}>
												{status}
											</option>
										))}
									</select>
								</div>

							</>
						)}
					</div>
				</div>
				<div className="flex justify-end space-x-2 p-5 border-t border-gray-200 bg-white rounded-b-lg">
					<button onClick={onClose} className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded border-none cursor-pointer">Cancel</button>
					<button onClick={submit} className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded border-none cursor-pointer">{mode === "add" ? "Save" : "Update"}</button>
				</div>
			</div>
		</div>
	);
}

function UploadForm({ isOpen, onClose, handleFinalFileUpload, banks }) {
	const [files, setFiles] = useState([]);
	const [uploadData, setUploadData] = useState([]);
	const [hasError, setHasError] = useState(false);
	const [employeeData, setEmployeeData] = useState([]);

	useEffect(() => {
		if (isOpen) {
			const fetchEmps = async () => {
				try {
					const res = await axios.get(`${domain}/api/Employee/single-data`, { headers: { 'Authorization': `Bearer ${token}` } });
					setEmployeeData(res.data);
				} catch (e) { console.error(e); }
			};
			fetchEmps();
			setFiles([]);
			setUploadData([]);
		}
	}, [isOpen]);

	const processFile = (file) => {
		const reader = new FileReader();
		reader.onload = (e) => {
			const data = new Uint8Array(e.target.result);
			const workbook = XLSX.read(data, { type: 'array' });
			const sheet = workbook.Sheets[workbook.SheetNames[0]];
			const json = XLSX.utils.sheet_to_json(sheet, { defval: '' });

			const processed = json.map(row => {
				const empId = row.EmployeeId ? row.EmployeeId.toString() : "";
				const foundEmp = employeeData.find(e => e.employeeId === empId);

				const bankNameInput = row.BankName ? row.BankName.toString().trim() : "";
				const foundBank = banks.find(b => b.bankName.toLowerCase() === bankNameInput.toLowerCase());

				let errorMsg = "";
				if (!empId) errorMsg += "Missing Emp Id. ";
				else if (!foundEmp) errorMsg += "Invalid Emp Id. ";

				if (!bankNameInput) errorMsg += "Missing Bank Name. ";
				else if (!foundBank) errorMsg += "Bank not in Master List. ";

				return {
					employeeId: empId,
					fullName: row.EmployeeName,
					employeePersonalInformationId: foundEmp ? foundEmp.id : 0,
					bankAccount: row.BankAccount || "",
					bankName: bankNameInput,
					bankId: foundBank ? foundBank.id : 0,
					errors: errorMsg
				};
			}).sort((a, b) => (b.errors ? 1 : 0) - (a.errors ? 1 : 0));

			setUploadData(processed);
			setHasError(processed.some(i => i.errors));
		};
		reader.readAsArrayBuffer(file);
	};

	const handleDrop = (e) => {
		e.preventDefault();
		const dropped = Array.from(e.dataTransfer.files);
		if (dropped.length > 0) {
			setFiles(dropped);
			processFile(dropped[0]);
		}
	};

	const handleFileSelect = (e) => {
		const selected = Array.from(e.target.files);
		if (selected.length > 0) {
			setFiles(selected);
			processFile(selected[0]);
		}
	};

	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-3 text-gray-700 overflow-auto">
			<div className="bg-gray-50 w-full md:w-[90%] lg:w-[60%] rounded-lg shadow-xl flex flex-col relative max-h-[90vh]">
				<div className="flex justify-between items-center px-6 py-2 border-b border-gray-200 bg-white rounded-t-lg">
					<h2 className="text-xl font-semibold text-gray-600">Upload Data</h2>
					<button onClick={onClose} className="text-gray-400 hover:text-gray-700 border-none bg-transparent cursor-pointer"><CloseIcon /></button>
				</div>
				<div className="p-6 overflow-y-auto space-y-4">
					<div onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.backgroundColor = "#e0e7ff"; }} onDragLeave={(e) => { e.preventDefault(); e.currentTarget.style.backgroundColor = "white"; }} onDrop={handleDrop} className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-white">
						<p className="text-gray-500 mb-4">Drag and drop Excel file here</p>
						<label className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded cursor-pointer">Browse<input type="file" className="hidden" accept=".xlsx, .xls" onChange={handleFileSelect} /></label>
					</div>
					{files.length > 0 && (
						<div className="space-y-4">
							<div className={`p-2 rounded text-white text-center font-bold ${hasError ? "bg-red-500" : "bg-green-500"}`}>{hasError ? "Data contains errors." : "Data is valid."}</div>
							<div className="max-h-60 overflow-auto border rounded bg-white">
								<table className="w-full text-sm text-left">
									<thead className="bg-gray-100 sticky top-0">
										<tr><th className="p-2">Emp ID</th><th className="p-2">Name</th><th className="p-2">Account</th><th className="p-2">Bank</th><th className="p-2">Status</th></tr>
									</thead>
									<tbody>
										{uploadData.map((row, idx) => (
											<tr key={idx} className={`border-b ${row.errors ? "bg-red-50" : ""}`}>
												<td className="p-2">{row.employeeId}</td>
												<td className="p-2">{row.fullName}</td>
												<td className="p-2">{row.bankAccount}</td>
												<td className="p-2">{row.bankName}</td>
												<td className="p-2 text-red-500">{row.errors}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</div>
					)}
				</div>
				<div className="flex justify-end space-x-2 p-5 border-t border-gray-200 bg-white rounded-b-lg">
					<button onClick={() => { setFiles([]); setUploadData([]); }} className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded border-none cursor-pointer">Clear</button>
					{files.length > 0 && !hasError && <button onClick={() => handleFinalFileUpload(uploadData)} className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded border-none cursor-pointer">Upload</button>}
				</div>
			</div>
		</div>
	);
}

export default EmployeeBankAccount;